import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useFriendships } from './useFriendships'
import { useUserEventInterests } from './useUserEventInterests'
import { useSocialEventData } from './useSocialEventData'
import { useEventsByIds } from './useEventsByIds'
import { useUserProfilesWithLoading } from './useUserProfiles'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { useFavoriteFriends } from '../contexts/FavoriteFriendsContext'
import { isEventActive } from '../lib/formatDate'
import { byStartDateAsc, compareByDisplayName } from '../lib/utils'
import type { Event } from '../types'
import { useMinuteNow } from './useMinuteNow'

export interface FriendOverview {
  uid: string
  favorite: boolean
  /** Their soonest upcoming event — one they're going to, else one they're interested in. */
  nextPlan?: Event
  /** Upcoming events both of you are going to or interested in. */
  sharedCount: number
}

const otherUid = (f: { userIds: string[] }, me: string | null) => f.userIds.find(u => u !== me) ?? ''

/**
 * The viewer's people for the Crew tab and the Friends list: explicit friends
 * with context (next plan, plans in common), incoming/outgoing requests, and
 * group mates who aren't friends yet — whose plans already show in facepiles.
 * Blocked users are left out everywhere.
 */
export function useFriendsOverview() {
  const { signedInUid } = useAuth()
  const friendships = useFriendships(signedInUid)
  const { eventStatuses: myStatuses, loading: statusesLoading } = useUserEventInterests(signedInUid)
  const social = useSocialEventData(myStatuses)
  const { isBlocked } = useBlockedUsers()
  const { favoriteIdSet, loading: favoritesLoading } = useFavoriteFriends()

  const eventIds = useMemo(() => [...social.friendInterests.keys()], [social.friendInterests])
  const { events, pending: eventsPending } = useEventsByIds(eventIds)

  const incomingIds = friendships.incoming.map(f => otherUid(f, signedInUid)).filter(uid => !isBlocked(uid))
  const outgoingIds = friendships.outgoing.map(f => otherUid(f, signedInUid)).filter(uid => !isBlocked(uid))
  const requestIds = [...incomingIds, ...outgoingIds]
  const { profiles: requestProfiles, loading: requestProfilesLoading } = useUserProfilesWithLoading(requestIds)
  const profiles = useMemo(
    () => ({ ...requestProfiles, ...social.mergedProfiles }),
    [requestProfiles, social.mergedProfiles],
  )

  const now = useMinuteNow()
  const { friends, groupMates } = useMemo(() => {
    const upcoming = events.filter(e => isEventActive(e, now)).sort(byStartDateAsc)
    const planFor = (uid: string) => {
      const going = upcoming.find(e => social.friendInterests.get(e.id)?.going.includes(uid))
      return going ?? upcoming.find(e => social.friendInterests.get(e.id)?.interested.includes(uid))
    }
    const sharedFor = (uid: string) =>
      upcoming.filter(e => {
        const b = social.friendInterests.get(e.id)
        const mine = myStatuses.get(e.id)
        return (mine === 'going' || mine === 'interested') && (b?.going.includes(uid) || b?.interested.includes(uid))
      }).length

    const explicit = new Set(friendships.friendIds)
    const friends: FriendOverview[] = friendships.friendIds
      .filter(uid => !isBlocked(uid))
      .map(uid => ({ uid, favorite: favoriteIdSet.has(uid), nextPlan: planFor(uid), sharedCount: sharedFor(uid) }))
      .sort((a, b) =>
        Number(b.favorite) - Number(a.favorite) ||
        b.sharedCount - a.sharedCount ||
        compareByDisplayName(a.uid, b.uid, profiles),
      )
    const pending = new Set(requestIds)
    const groupMates = social.friendIds
      .filter(uid => uid !== signedInUid && !explicit.has(uid) && !pending.has(uid) && !isBlocked(uid))
      .sort((a, b) => compareByDisplayName(a, b, profiles))
    return { friends, groupMates }
    // requestIds is derived from friendships each render; friendships covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, now, social.friendInterests, social.friendIds, myStatuses, friendships, isBlocked, favoriteIdSet, profiles, signedInUid])

  // Friends are sorted by favorite, plans in common and name, each fed by its
  // own subscription — hold the skeleton until all have delivered so the list
  // doesn't paint in one order and reshuffle a beat later.
  const orderPending = !!signedInUid && (
    favoritesLoading || statusesLoading || social.socialLoading || social.peersPending || eventsPending
  )

  return {
    friends,
    groupMates,
    incomingIds,
    outgoingIds,
    profiles,
    friendInterests: social.friendInterests,
    myStatuses,
    loading: friendships.loading || orderPending,
    profilesLoading: requestProfilesLoading,
    sendRequest: friendships.sendRequest,
    acceptRequest: friendships.acceptRequest,
    removeFriend: friendships.removeFriend,
  }
}
